import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Mail, Linkedin } from 'lucide-react';
import { UserRole, AuthFormProps } from '@/types/auth';
import { useAuthStore } from '@/stores/authStore';
import { useLoginConfig } from '@/hooks/useLoginConfig';
import { useRequestPasswordReset } from '@/hooks/auth/useRequestPasswordReset';
import useFetch from '@/hooks/useFetch';
import { BrandMark } from '@/components/shared/BrandMark';
import { PasswordInput } from '@/components/ui/password-input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { isStaffRole } from '@/types/auth';
import { INVALID_CREDENTIALS_REASON, loginErrorMessage, loginErrorReason, loginReasonMessage } from '@/lib/errors';
import { rememberOAuthRedirect, requestedRedirect } from '@/lib/accountActivation';
import { B2C_PLATFORMS, b2cPath, currentPlatform, ePath, PLATFORMS, type Platform } from '@/utils/paths';
import { platformBrandLabel } from '@/config/resources';
import { platformPortalHome, platformRedirectPath, type EntrepreneurPlatform } from '@/config/entrepreneurPlatforms';

const LAST_AUTH_COOKIE = 'terminus_last_auth';

function getLastAuthMethod(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${LAST_AUTH_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function setLastAuthMethod(method: string): void {
  const expires = new Date();
  expires.setDate(expires.getDate() + 90);
  document.cookie = `${LAST_AUTH_COOKIE}=${encodeURIComponent(method)}; expires=${expires.toUTCString()}; path=/; SameSite=Lax`;
}

interface LoginPageProps extends AuthFormProps {
  role: UserRole;
}

const getUIConfig = (
  role: UserRole,
  t: (key: string) => string,
  platform: Platform | null,
) => {
  const isStaff = isStaffRole(role);
  return {
    brandLabel: platformBrandLabel(platform),
    subtitle: platform ? t('login.poweredByVess') : null,
    footerText: isStaff ? t('login.contactSupport') : null,
    helpEmail: isStaff ? import.meta.env.VITE_SUPPORT_EMAIL : null,
  };
};

export const LoginPage: React.FC<LoginPageProps> = ({
  role,
  onSuccess,
  onError
}) => {
  const { t } = useTranslation();
  const { loginWithCredentials, redirectToDashboard, isLoading, error } = useAuthStore();
  // The host (or the URL prefix in dev) decides which platform's login this is.
  // `?platform=` only fills in for legacy links landing on the neutral portal.
  const requestedPlatform = new URLSearchParams(window.location.search).get('platform');
  const platform: Platform | null = currentPlatform()
    ?? (PLATFORMS.includes(requestedPlatform as Platform) ? requestedPlatform as Platform : null);
  // login_configurations is keyed by path_prefix, NOT by role. The rows in
  // the DB are always stored platform-qualified (`/gaia/entrepreneur/login`,
  // `/foundershub/entrepreneur/login`) so the lookup MUST pass that same
  // shape — `ePath('/login')` collapses to `/entrepreneur/login` on the
  // platform's own host and the request 404s. Send a fully prefixed path
  // for both surfaces.
  const loginPathPrefix =
    role === UserRole.ENTREPRENEUR && (platform === 'gaia' || platform === 'foundershub')
      ? `/${platform}/entrepreneur/login`
      : '/login';
  const { config: authConfig, isLoading: configLoading } = useLoginConfig(loginPathPrefix);
  const { doFetch } = useFetch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [isSocialLoading, setIsSocialLoading] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [credentialsRejected, setCredentialsRejected] = useState(false);
  const lastAuthMethod = getLastAuthMethod();
  const errorCode = new URLSearchParams(window.location.search).get('error');
  const shownError = error ?? loginReasonMessage(errorCode);

  const [isResetDialogOpen, setIsResetDialogOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSubmitted, setResetSubmitted] = useState(false);
  const [isResetSubmitting, setIsResetSubmitting] = useState(false);
  const { request: requestPasswordReset } = useRequestPasswordReset();

  const uiConfig = getUIConfig(role, t, platform);

  if (configLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-foreground" />
      </div>
    );
  }

  if (!authConfig) {
    return <div className="min-h-screen bg-background flex items-center justify-center text-muted-foreground">{t('login.invalidConfiguration')}</div>;
  }

  // Entrepreneur authentication is platform-specific. The role-level config
  // still exposes the legacy endpoint for compatibility, but using it here
  // loses the login origin and sends Gaia participants through FoundersHub's
  // onboarding state machine — so it is the last resort, for a founder whose
  // URL names no platform at all.
  const isFounder = role === UserRole.ENTREPRENEUR;
  // Narrowed to the B2C subset: terminus has no founders' portal, so it must
  // never reach `/entrepreneur/auth/{platform}/login`.
  const founderPlatform: EntrepreneurPlatform | null =
    isFounder && (platform === 'gaia' || platform === 'foundershub') ? platform : null;
  const passwordLoginEnabled = authConfig.allow_password_login;
  const passwordLoginEndpoint = founderPlatform
    ? `/entrepreneur/auth/${founderPlatform}/login`
    : authConfig.password_login_endpoint;
  // Self-signup CTA: shown only when the login URL allows it (signup_mode
  // === "open"). For B2C we link to the platform-specific signup page; for
  // staff the CTA is hidden (staff only come in via invitation).
  // Self-signup CTA: shown only when the login URL allows it (signup_mode
  // === "open"). The FE page URL is built locally with ePath() — `at('/signup')`
  // in App.tsx mounts at `/signup` in host mode and `/<platform>/entrepreneur/signup`
  // in prefix mode. Signup is B2C-only today; if staff/terminus ever open,
  // they mount their own /<portal>/signup route and the CTA below needs
  // a portal-specific path (not ePath('/signup')). For now this works.
  const signupMode = authConfig.signup_mode;
  const signupEnabled = signupMode === "open" && isFounder;
  const signupPath = signupEnabled ? ePath('/signup') : null;
  // Gaia supports both credentials and LinkedIn. An invited user may have
  // completed password onboarding, while an existing LinkedIn identity must
  // be able to accept the invitation without creating a password.
  const oauthProviders = authConfig.oauth_providers;
  const offersEntrepreneurLogin =
    isStaffRole(role) &&
    platform !== null &&
    (B2C_PLATFORMS as readonly string[]).includes(platform);

  const handlePasswordLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!passwordLoginEnabled || !passwordLoginEndpoint) return;
    if (!email.trim() || !password.trim()) return;

    try {
      setIsPasswordLoading(true);
      setCredentialsRejected(false);
      const user = await loginWithCredentials({ email, password }, passwordLoginEndpoint);
      setLastAuthMethod('password');
      if (onSuccess) {
        onSuccess(user);
      } else {
        if (founderPlatform === 'gaia') {
          // Gaia's participant guard sends pending email accounts to its own
          // verification screen; active accounts land on Gaia's B2C portal.
          // redirectToDashboard honors a `?redirect=` param when present.
          redirectToDashboard(role, platformPortalHome('gaia'));
        } else if (founderPlatform === 'foundershub') {
          // The FoundersHub host root is its STAFF portal: a founder sent to
          // `/` is resolved against the staff `/me`. The founder auth-success
          // page routes on by account state (consent → onboarding → dashboard).
          redirectToDashboard(role, b2cPath('foundershub', '/auth/success'));
        } else if (isFounder && !founderPlatform) {
          // No platform in the URL: resolve it from the memberships instead,
          // preferring FoundersHub when the founder holds both.
          const accessResponse = await doFetch<{ accesses: Array<{
            platform: 'foundershub' | 'gaia';
            status: string;
            is_active: boolean;
            is_disabled: boolean;
            redirect_path: string | null;
          }> }>('/entrepreneur/auth/accesses');
          const available = accessResponse.accesses.filter((access) => !access.is_disabled);
          const targetAccess = available.find((access) => access.platform === 'foundershub')
            ?? available.find((access) => access.platform === 'gaia');
          if (targetAccess && !requestedRedirect()) {
            window.location.href = platformRedirectPath(targetAccess);
          } else {
            redirectToDashboard(role);
          }
        } else {
          redirectToDashboard(role);
        }
      }
    } catch (error: any) {
      setCredentialsRejected(loginErrorReason(error) === INVALID_CREDENTIALS_REASON);
      if (onError) onError(loginErrorMessage(error));
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handlePasswordResetRequest = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail.trim() || isResetSubmitting) return;
    setIsResetSubmitting(true);
    try {
      // The endpoint always answers `sent: true` regardless of whether the
      // email is on file -- anti-enumeration. We do the same on the client:
      // the same confirmation toast answers both "we sent you a reset" and
      // "no, we didn't, but we won't tell you which".
      await requestPasswordReset({
        email: resetEmail.trim(),
        login_path_prefix: loginPathPrefix,
        platform,
      });
      setResetSubmitted(true);
    } catch (error) {
      // Same mask on the client. A 500 from Brevo would otherwise tell the
      // caller "this email exists" the same way a 200 vs a different 500 would.
      setResetSubmitted(true);
    } finally {
      setIsResetSubmitting(false);
    }
  };

  const openResetDialog = () => {
    setResetEmail(email);
    setResetSubmitted(false);
    setIsResetDialogOpen(true);
  };

  const handleSocialLogin = async (authUrlEndpoint: string, provider: string) => {
    try {
      setIsSocialLoading(true);
      setSelectedProvider(provider);
      // Every social login on the FE carries `intent=login`: /login only signs
      // in, /signup only registers. A founder's LinkedIn goes through its
      // platform's own endpoint, whose backend-minted `state` carries the
      // platform and the intent; a founder without an account comes back
      // with `?error=no_account`.
      let endpointWithOrigin: string;
      if (founderPlatform && provider === 'linkedin') {
        endpointWithOrigin = `/entrepreneur/auth/${founderPlatform}/linkedin/url?intent=login`;
      } else {
        const separator = authUrlEndpoint.includes('?') ? '&' : '?';
        const pathPrefix = role === UserRole.ENTREPRENEUR ? ePath('') : '';
        const stateBase = window.location.origin + pathPrefix;
        endpointWithOrigin = `${authUrlEndpoint}${separator}state=${encodeURIComponent(stateBase)}&intent=login`;
      }
      const data = await doFetch<{ authorization_url?: string }>(endpointWithOrigin);
      if (data.authorization_url) {
        setLastAuthMethod(provider);
        rememberOAuthRedirect();
        window.location.href = data.authorization_url;
      } else {
        throw new Error('No authorization URL received');
      }
    } catch (error: any) {
      if (onError) onError(error.message || 'Failed to initiate authentication');
      setIsSocialLoading(false);
      setSelectedProvider(null);
    }
  };

  const getSocialButton = (provider: { provider: string; auth_url_endpoint: string }) => {
    const isLinkedIn = provider.provider === 'linkedin';
    const isMicrosoft = provider.provider === 'microsoft';
    const isGoogle = provider.provider === 'gmail';
    const isActive = isSocialLoading && selectedProvider === provider.provider;
    const isLastUsed = lastAuthMethod === provider.provider;

    return (
      <div key={provider.provider} className="relative">
        <button
          onClick={() => handleSocialLogin(provider.auth_url_endpoint, provider.provider)}
          disabled={isSocialLoading || isLoading}
          className={cn(
            "w-full h-12 flex items-center justify-center gap-3 rounded-full font-medium text-sm transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed border text-foreground lowercase",
            isLastUsed ? "border-foreground/40" : "border-border",
            isLinkedIn ? "hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]" : "hover:border-foreground/40 hover:bg-accent"
          )}
        >
          {isActive ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              {isLinkedIn && <Linkedin className="h-5 w-5" />}
              {isMicrosoft && (
                <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zM24 11.4H12.6V0H24v11.4z"/>
                </svg>
              )}
              {isGoogle && (
                <svg className="h-5 w-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
              )}
              {t('login.continueWith', { provider: provider.provider.charAt(0).toUpperCase() + provider.provider.slice(1) })}
            </>
          )}
        </button>
        {isLastUsed && (
          <span className="absolute -top-2 right-3 text-[10px] text-muted-foreground bg-background px-1.5 lowercase">
            {t('login.lastUsed')}
          </span>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-[320px]">
        <div className="text-center mb-12">
          <h1><BrandMark label={uiConfig.brandLabel} /></h1>
          {uiConfig.subtitle && (
            <p className="text-muted-foreground text-sm mt-3 lowercase">{uiConfig.subtitle}</p>
          )}
        </div>

        {oauthProviders && oauthProviders.length > 0 && (
          <div className="space-y-3 mb-8">
            {oauthProviders.map(getSocialButton)}
          </div>
        )}

        {passwordLoginEnabled && oauthProviders && oauthProviders.length > 0 && (
          <div className="relative my-8">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center">
              <span className="bg-background px-4 text-muted-foreground text-sm">{t('login.or')}</span>
            </div>
          </div>
        )}

        {passwordLoginEnabled && (
          <>
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              <div className="relative">
                <label className="block text-muted-foreground text-sm font-medium mb-2 lowercase">
                  {t('login.email')}
                </label>
                <div className="relative">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    type="email"
                    placeholder="you@company.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full h-12 pl-11 pr-4 bg-muted rounded-md text-foreground placeholder:text-muted-foreground text-sm outline-none border border-transparent focus:border-border transition-colors"
                    disabled={isPasswordLoading || isLoading}
                    autoComplete="username"
                  />
                </div>
              </div>

              <div>
                <label className="block text-muted-foreground text-sm font-medium mb-2 lowercase">
                  {t('login.password')}
                </label>
                <PasswordInput
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={isPasswordLoading || isLoading}
                  autoComplete="current-password"
                />
              </div>

              <div className="relative mt-6">
                <button
                  type="submit"
                  disabled={isPasswordLoading || isLoading}
                  className="w-full h-12 bg-primary hover:bg-primary/90 text-primary-foreground font-bold rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed lowercase vess-btn-primary"
                >
                  {isPasswordLoading ? (
                    <Loader2 className="h-5 w-5 animate-spin mx-auto" />
                  ) : (
                    t('login.logIn')
                  )}
                </button>
                {lastAuthMethod === 'password' && (
                  <span className="absolute -top-2 right-3 text-[10px] text-muted-foreground bg-background px-1.5 lowercase">
                    {t('login.lastUsed')}
                  </span>
                )}
              </div>
            </form>
            <div className="mt-3 text-center">
              <button
                type="button"
                onClick={openResetDialog}
                className="text-xs text-muted-foreground hover:text-foreground transition-colors lowercase underline underline-offset-4"
              >
                {t('login.forgotPassword', '¿Olvidaste tu contraseña?')}
              </button>
            </div>
          </>
        )}

        {shownError && (
          <p className="text-center text-red-400 text-sm mt-4">
            {shownError}
            {!error && errorCode === 'no_account' && signupPath && (
              <>
                {' '}
                <a
                  href={signupPath}
                  className="text-foreground hover:text-foreground/80 transition-colors underline underline-offset-4"
                >
                  {t('login.signUpLink')}
                </a>
              </>
            )}
          </p>
        )}

        {credentialsRejected && offersEntrepreneurLogin && (
          <p className="text-center text-muted-foreground text-sm mt-2">
            {t('login.entrepreneurHint')}{' '}
            <a
              href={ePath('/login')}
              className="text-foreground hover:text-foreground/80 transition-colors underline underline-offset-4"
            >
              {t('login.entrepreneurHintLink')}
            </a>
          </p>
        )}

        {/* Self-signup CTA: shown only when the login URL's `signup_mode` allows
            self-signup. Mirrors the staff password-reset line below. Staff URLs
            are `invitation_only` (no signup); B2C URLs default to `open` and
            the admin can flip to `invitation_only`/`disabled` without code. */}
        {signupEnabled && (
          <p className="text-center text-muted-foreground text-sm mt-8">
            <a
              href={signupPath}
              className="text-foreground hover:text-foreground/80 transition-colors underline underline-offset-4"
            >
              {t('login.notAMember', '¿No eres miembro? Sign up')}
            </a>
          </p>
        )}

        <Dialog open={isResetDialogOpen} onOpenChange={setIsResetDialogOpen}>
          <DialogContent>
            {resetSubmitted ? (
              <>
                <DialogHeader>
                  <DialogTitle>
                    {t('login.resetDialog.confirmTitle', 'Revisa tu correo')}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      'login.resetDialog.confirmDesc',
                      'Si la dirección está registrada, te hemos enviado un enlace para restablecer la contraseña. Si no llega en unos minutos, revisa la carpeta de spam.'
                    )}
                  </DialogDescription>
                </DialogHeader>
                <DialogFooter>
                  <Button
                    type="button"
                    onClick={() => setIsResetDialogOpen(false)}
                    className="vess-btn-primary"
                  >
                    {t('common.close', 'Cerrar')}
                  </Button>
                </DialogFooter>
              </>
            ) : (
              <form onSubmit={handlePasswordResetRequest}>
                <DialogHeader>
                  <DialogTitle>
                    {t('login.resetDialog.title', 'Restablecer contraseña')}
                  </DialogTitle>
                  <DialogDescription>
                    {t(
                      'login.resetDialog.desc',
                      'Dinos la dirección con la que inicias sesión y te enviaremos un enlace para crear una contraseña nueva.'
                    )}
                  </DialogDescription>
                </DialogHeader>
                <div className="py-2">
                  <label className="block text-muted-foreground text-sm font-medium mb-2 lowercase">
                    {t('login.email')}
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <input
                      type="email"
                      autoFocus
                      placeholder="you@company.com"
                      value={resetEmail}
                      onChange={(e) => setResetEmail(e.target.value)}
                      className="w-full h-12 pl-11 pr-4 bg-muted rounded-md text-foreground placeholder:text-muted-foreground text-sm outline-none border border-transparent focus:border-border transition-colors"
                      autoComplete="email"
                      disabled={isResetSubmitting}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setIsResetDialogOpen(false)}
                    disabled={isResetSubmitting}
                  >
                    {t('common.cancel', 'Cancelar')}
                  </Button>
                  <Button
                    type="submit"
                    disabled={!resetEmail.trim() || isResetSubmitting}
                    className="vess-btn-primary"
                  >
                    {isResetSubmitting ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      t('login.resetDialog.submit', 'Enviar enlace')
                    )}
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* This page is the STAFF login and it is what `/` serves on a product
            host, so an entrepreneur typing gaia.<domain> lands here and every
            correct password answers 401 -- the backend filters by staff roles,
            which never include ENTREPRENEUR. Their login exists at
            `/entrepreneur/login` and nothing linked to it. */}
        {offersEntrepreneurLogin && (
          <p className="text-center text-muted-foreground text-sm mt-8">
            <a
              href={ePath('/login')}
              className="text-foreground hover:text-foreground/80 transition-colors underline underline-offset-4"
            >
              {t('login.entrepreneurLink', '¿Eres emprendedor? Entra aquí')}
            </a>
          </p>
        )}
      </div>

      {uiConfig.footerText && (
        <p className="text-muted-foreground text-xs mt-12">
          {uiConfig.footerText}
          {uiConfig.helpEmail && (
            <a href={`mailto:${uiConfig.helpEmail}`} className="text-foreground hover:text-foreground/80 ml-1 transition-colors">
              {uiConfig.helpEmail}
            </a>
          )}
        </p>
      )}
    </div>
  );
};
